import { useEffect, useState } from 'react';
import api from '@/api';
import { Navigation, MapPin } from 'lucide-react';

const stages = ['Pending', 'Confirmed', 'In Transit', 'At Port', 'Delivered'];

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700' },
    active: { label: 'Active', cls: 'bg-blue-50 text-blue-700' },
    in_transit: { label: 'In Transit', cls: 'bg-indigo-50 text-indigo-700' },
    at_port: { label: 'At Port', cls: 'bg-purple-50 text-purple-700' },
    delivered: { label: 'Delivered', cls: 'bg-green-50 text-green-700' },
  };
  const s = map[status] || map.pending;
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

export default function Tracking({
  pageTitle = 'Shipment tracking',
  pageSubtitle = 'Track your active shipments in real time',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/freight/').then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const getStageIndex = (status) => {
    const map = { pending: 0, active: 1, in_transit: 2, at_port: 3, delivered: 4 };
    return map[status] ?? 0;
  };

  return (
    <div className="space-y-5" data-testid="tracking-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="text-slate-500 text-sm mt-0.5">{pageSubtitle}</p>
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-36 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Navigation className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No shipments to track</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <div key={item._id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`tracking-card-${item._id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-900">{item.origin}</span>
                    <span className="text-slate-300">→</span>
                    <span className="text-sm font-semibold text-slate-900">{item.destination}</span>
                  </div>
                  <p className="text-xs text-slate-400">{item.cargo_type} • {item.container_type} • {item.incoterm}</p>
                </div>
                <StatusBadge status={item.status} />
              </div>

              {/* Progress bar */}
              <div className="relative mt-4">
                <div className="flex justify-between mb-2">
                  {stages.map((stage, i) => {
                    const current = getStageIndex(item.status);
                    return (
                      <div key={stage} className="flex flex-col items-center flex-1">
                        <div className={`w-3 h-3 rounded-full border-2 z-10 relative ${i <= current ? 'bg-green-600 border-green-600' : 'bg-white border-slate-300'}`} />
                        <p className={`text-xs mt-1.5 text-center leading-tight ${i <= current ? 'text-green-700 font-semibold' : 'text-slate-400'}`}>{stage}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="absolute top-1.5 left-0 right-0 h-0.5 bg-slate-200 -z-0">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${(getStageIndex(item.status) / (stages.length - 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
